/**
 * Redux slice for managing shift reassignment recommendations.
 * Uses Redux Toolkit's createEntityAdapter for normalized state management.
 */

// External imports
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { reassignmentRecommendationsRoute } from 'api';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import {
  RECOMMENDATIONS_SHOW_LIMIT,
  SHIFT_REASSIGNMENT_QUERY_TIMEOUT,
} from './constants';
import { orderReasons } from './helper';
// Internal imports
import { ReassignmentState, Recommendation } from './types';

// Entity adapter setup
const reassignmentAdapter = createEntityAdapter<Recommendation>({
  selectId: recommendation => recommendation.job_id,
});

const shiftReassignmentQueryTimeout = (delay: number) =>
  new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Query timed out')), delay)
  );

/**
 * Async thunk for fetching reassignment recommendations for a given shift
 * @param shiftId - The ID of the shift to get recommendations for
 */
export const fetchReassignmentRecommendations = createAsyncThunk(
  'reassignment/fetchRecommendations',
  async (shiftId: number) => {
    try {
      const response = await Promise.race([
        fetchJSON(reassignmentRecommendationsRoute(shiftId)),
        shiftReassignmentQueryTimeout(SHIFT_REASSIGNMENT_QUERY_TIMEOUT),
      ]);
      return Promise.resolve(response.recommendations);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

// Initialize state with adapter
export const initialState: ReassignmentState =
  reassignmentAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

/**
 * Redux slice configuration with reducers and extra reducers for async actions
 */
const slice = createSlice({
  name: 'reassignment',
  initialState,
  reducers: {
    clearRecommendations: state => {
      reassignmentAdapter.removeAll(state);
    },
  },
  extraReducers: builder => {
    builder
      // Handle pending state
      .addCase(fetchReassignmentRecommendations.pending, state => {
        state.status = 'pending';
      })
      // Handle successful fetch
      .addCase(fetchReassignmentRecommendations.fulfilled, (state, action) => {
        const recommendations = Array.isArray(action.payload)
          ? action.payload
          : [];
        const items = recommendations.slice(0, RECOMMENDATIONS_SHOW_LIMIT).map(
          (item: Recommendation): Recommendation => ({
            ...item,
            job_id: item.job_id?.toString(),
            reasons: orderReasons(item.reasons),
          })
        );

        reassignmentAdapter.setAll(state, items);
        state.status = 'succeeded';
      })
      // Handle fetch failure
      .addCase(fetchReassignmentRecommendations.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch recommendations';
        state.status = 'failed';
      });
  },
});

// Memoized selectors for use with root state
export const { selectAll } = reassignmentAdapter.getSelectors(
  (state: RootState) => state.get('scheduleBuilderSchedule').reassignment
);

export const selectReassignmentRecommendations = (state: RootState) =>
  selectAll(state);

// Custom selectors
export const selectReassignmentStatus = (state: RootState) =>
  state.get('scheduleBuilderSchedule').reassignment.status;

// Export actions and reducer
export const { reducer } = slice;
export const { clearRecommendations } = slice.actions;
